import React, { useEffect, useState, useRef } from "react";
import { useKeycloak } from "@react-keycloak/web";

const SessionTimeoutManager = ({ timeoutMinutes = 30, warningMinutes = 2 }) => {
    const { keycloak } = useKeycloak();
    const [showModal, setShowModal] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const timeoutRef = useRef(null);
    const warningRef = useRef(null);

    const startSessionTimers = () => {
        clearTimers();

        // Show warning before session expires
        warningRef.current = setTimeout(() => {
            setShowModal(true);
        }, (timeoutMinutes - warningMinutes) * 60 * 1000);

        // End session after timeout
        timeoutRef.current = setTimeout(() => {
            handleLogout();
        }, timeoutMinutes * 60 * 1000);
    };

    const clearTimers = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (warningRef.current) clearTimeout(warningRef.current);
    };

    const handleContinue = () => {
        setShowModal(false);
        startSessionTimers(); // Reset session timers
    };

    const handleLogout = () => {
        clearTimers();
        setShowModal(false);
        keycloak.logout();
    };

    useEffect(() => {
        if (keycloak.authenticated) {
            startSessionTimers();
        }

        return () => clearTimers();
    }, [keycloak.authenticated]);

    // Listen for messages from assessment windows to pause/resume timers
    useEffect(() => {
        const handleMessage = (event) => {
            const data = event?.data;
            if (!data || typeof data !== "object") return;

            // When assessment window opens, pause the session timeout
            if (data.type === "assessment:started") {
                console.log("⏸️ Session timeout paused - assessment in progress");
                setIsPaused(true);
                clearTimers();
            }
            // When assessment is complete or window closes, resume timeout
            else if (data.type === "assessment:complete" || data.type === "assessment:closed") {
                console.log("▶️ Session timeout resumed");
                setIsPaused(false);
                if (keycloak.authenticated) {
                    startSessionTimers();
                }
            }
            // Reset timers on any activity from assessment window
            else if (data.type === "assessment:activity" && keycloak.authenticated && !isPaused) {
                startSessionTimers();
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [keycloak.authenticated, isPaused]);

    return showModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm text-center">
                <h2 className="text-xl font-semibold mb-4">Session Expiring</h2>
                <p className="mb-4 text-gray-700">
                    Your session is about to expire. Do you want to continue?
                </p>
                <div className="flex justify-center gap-4">
                    <button
                        onClick={handleContinue}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                        Continue
                    </button>
                    <button
                        onClick={handleLogout}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                        Logout
                    </button>
                </div>
            </div>
        </div>
    ) : null;
};

export default SessionTimeoutManager;
